import React from 'react';
import { Link } from 'react-router-dom';
import { SEV_COLORS, DARK_BASE } from './constants';
import { FindingWithProject } from './types';
import SevBadge from './SevBadge';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';

interface Top10TableProps {
  findings: FindingWithProject[];
}

const cvssColor = (score?: number): string => {
  if (score == null) return 'inherit';
  if (score >= 9) return SEV_COLORS.Critical;
  if (score >= 7) return SEV_COLORS.High;
  return 'inherit';
};

const truncate = (s: string | undefined, max: number): string => {
  if (!s) return '—';
  return s.length > max ? s.slice(0, max - 2) + '…' : s;
};

const findingUrl = (f: FindingWithProject): string | null => {
  if (!f.canopyProjectId || f.phase == null) return null;
  return `/projects/${f.testId}/${f.canopyProjectId}/reports/${f.phase}/finding/${f.id}`;
};

const reportUrl = (f: FindingWithProject): string | null => {
  if (!f.canopyProjectId) return null;
  return `/projects/${f.testId}/${f.canopyProjectId}/reports`;
};

const Top10Table: React.FC<Top10TableProps> = ({ findings }) => {
  const { isDark } = useChartTheme();
  const linkColor = isDark ? 'rgba(255,255,255,0.9)' : DARK_BASE;

  const top10 = [...findings]
    .filter(f => f.cvss3_score != null)
    .sort((a, b) => (b.cvss3_score ?? 0) - (a.cvss3_score ?? 0))
    .slice(0, 10);

  const cvss9Plus = findings.filter(f => (f.cvss3_score ?? 0) >= 9).length;

  return (
    <ChartCard
      title="Top 10 vulnerabilities"
      sub="Ordered by CVSS score desc - all filtered projects"
      extra={
        cvss9Plus > 0 ? (
          <span style={{ color: SEV_COLORS.Critical, fontWeight: 700, fontSize: 13 }}>
            {cvss9Plus} CVSS 9+
          </span>
        ) : undefined
      }
    >
      {top10.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ width: 24 }}>#</th>
                <th>Vulnerability</th>
                <th>Project</th>
                <th>CVSS</th>
                <th>Sev</th>
                <th style={{ width: 24 }}>#</th>
                <th>Vulnerability</th>
                <th>Project</th>
                <th>CVSS</th>
                <th>Sev</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => {
                const left = top10[i];
                const right = top10[i + 5];
                return (
                  <tr key={i}>
                    <td className="text-muted">{i + 1}</td>
                    <td>
                      {left && findingUrl(left) ? (
                        <Link to={findingUrl(left)!} className="fw-semibold text-decoration-none" title={left.title} style={{ color: linkColor }}>
                          {truncate(left.title, 28)}
                        </Link>
                      ) : (
                        <span className="fw-semibold" title={left?.title} style={{ color: 'var(--bs-body-color)' }}>
                          {truncate(left?.title, 28)}
                        </span>
                      )}
                    </td>
                    <td className="text-muted small">
                      {left && reportUrl(left) ? (
                        <Link to={reportUrl(left)!} className="text-decoration-none text-muted" title={left.projectName}>
                          {left.projectName}
                        </Link>
                      ) : (
                        left?.projectName ?? '—'
                      )}
                    </td>
                    <td style={{ fontWeight: 700, color: cvssColor(left?.cvss3_score) }}>
                      {left?.cvss3_score?.toFixed(1) ?? '—'}
                    </td>
                    <td>{left && <SevBadge rating={left.rating} />}</td>

                    {right ? (
                      <>
                        <td className="text-muted">{i + 6}</td>
                        <td>
                          {findingUrl(right) ? (
                            <Link to={findingUrl(right)!} className="fw-semibold text-decoration-none" title={right.title} style={{ color: linkColor }}>
                              {truncate(right.title, 28)}
                            </Link>
                          ) : (
                            <span className="fw-semibold" title={right.title} style={{ color: 'var(--bs-body-color)' }}>
                              {truncate(right.title, 28)}
                            </span>
                          )}
                        </td>
                        <td className="text-muted small">
                          {reportUrl(right) ? (
                            <Link to={reportUrl(right)!} className="text-decoration-none text-muted" title={right.projectName}>
                              {right.projectName}
                            </Link>
                          ) : (
                            right.projectName
                          )}
                        </td>
                        <td style={{ fontWeight: 700, color: cvssColor(right.cvss3_score) }}>
                          {right.cvss3_score?.toFixed(1) ?? '—'}
                        </td>
                        <td><SevBadge rating={right.rating} /></td>
                      </>
                    ) : (
                      <td colSpan={5} />
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-muted text-center py-5 small">
          No vulnerability data with CVSS scores for the current filters
        </div>
      )}
    </ChartCard>
  );
};

export default Top10Table;
